import { useTranslation } from 'react-i18next';
import classes from './HeadingWidget.module.css';
import { componentsTestIds } from '@components/testids';

/**
 * TV1-2291 (SDD §5 · t21) — the section divider. The SECOND dataless widget,
 * after `text`.
 *
 * No cube, no measures, no Cube query, no fetch — it extends `UiWidgetBaseSchema`
 * in the domain for exactly the reason `text` does, and it is a member of
 * `DATALESS_WIDGET_TYPES` here, so `WidgetLayoutComponent.getWidgetData` returns
 * the `DATALESS_WIDGET_DATA` sentinel for it and never touches Cube.
 *
 * ## The widget's own `title` IS the content
 *
 * There is deliberately NO separate content field, and the domain schema declares
 * none: `UiWidgetHeadingSchema` adds nothing but `type` on top of the base, whose
 * `title` is the section name. A second field would mean two places to type the
 * same string and a rule about which one wins.
 *
 * The consequence is that the LAYOUT CHROME must not also draw the title — a
 * heading would render its text twice, once in the tile header and once here.
 * `CHROMELESS_WIDGET_TYPES` (`Widgets/types.ts`) is what the viewer and the editor
 * consult to suppress that header; the prototype does the same
 * (`prototype-b-grid.html:1214`, `showHead`).
 *
 * ## Why a `<h3>` and a rule
 *
 * The `<h3>` is semantic, not decorative: a report read top-to-bottom has a
 * document outline, and a screen reader needs the section breaks to be in it. The
 * rule is what makes the break VISIBLE at a glance without the tile needing a
 * border, and it flexes so the divider always reaches the right edge of whatever
 * span the author gave the widget.
 */

export type HeadingWidgetProps = {
  /** The section name — `widget.definition.title`, threaded through by `WebWidget`. */
  title?: string;
};

export const HeadingWidget = ({ title }: HeadingWidgetProps ) => {
  const { t } = useTranslation( 'widgets' );

  // A heading with no title yet is a legitimate intermediate state while
  // authoring (the same call `TextWidget` makes for empty content). The rule is
  // still drawn so the widget reads as a section break rather than as a blank
  // tile, and the placeholder says which widget it is.
  if ( !title?.trim()) {
    return (
      <div className={classes.container} data-testid={componentsTestIds.headingWidgetEmpty}>
        <span className={classes.emptyTitle}>
          {t( 'widget_heading_empty', 'Untitled section' )}
        </span>
        <span className={classes.rule} />
      </div>
    );
  }

  return (
    <div className={classes.container} data-testid={componentsTestIds.headingWidget}>
      <h3 className={classes.title}>{title}</h3>
      <span className={classes.rule} />
    </div>
  );
};
